import React from "react";
import PageHero, { usePageMeta } from "@/components/marketing/PageHero";
import { getWhy, getCompliance } from "@/data/site";
import Reveal from "@/components/marketing/Reveal";
import { MapPin, Building2 } from "lucide-react";
import { useI18n } from "@/context/I18nContext";

const ABOUT_IMG = "https://images.unsplash.com/photo-1573164574511-73c773193279?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

export default function ChiSiamo() {
  const { lang } = useI18n();
  const L = (it, en, de) => (lang === "en" ? en : lang === "de" ? (de ?? it) : it);
  usePageMeta(L("Chi siamo", "About us", "Über uns"), L("Recovers Crypto: un servizio professionale non-custodial di assistenza al recupero dell'accesso ai wallet crypto.", "Recovers Crypto: a professional non-custodial crypto wallet access recovery service.", "Recovers Crypto: ein professioneller Non-custodial-Dienst zur Wiederherstellung des Zugriffs auf Krypto-Wallets."));
  const why = getWhy(lang);
  const compliance = getCompliance(lang);
  return (
    <div>
      <PageHero eyebrow={L("Chi siamo", "About us", "Über uns")} title={L("Competenza tecnica al servizio dei titolari", "Technical expertise at the service of owners", "Technische Kompetenz im Dienst der Inhaber")}
        subtitle={L("Recovers Crypto assiste titolari verificati e soggetti legalmente autorizzati nel recupero dell'accesso ai propri wallet, con un approccio riservato e non-custodial.", "Recovers Crypto assists verified owners and legally authorized parties in recovering access to their wallets, with a confidential, non-custodial approach.", "Recovers Crypto unterstützt verifizierte Inhaber und rechtlich autorisierte Personen bei der Wiederherstellung des Zugriffs auf ihre Wallets – vertraulich und non-custodial.")} />
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-20">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-stretch mb-16" data-testid="company-location">
          <div className="rounded-xl border border-white/10 bg-navy-paper p-8">
            <h3 className="font-heading text-xl font-medium text-white flex items-center gap-2">
              <Building2 className="h-5 w-5 text-gold" />{L("Sede legale", "Registered office", "Firmensitz")}
            </h3>
            <div className="mt-5 space-y-4 text-sm">
              <div className="flex items-start gap-3">
                <MapPin className="h-5 w-5 text-cyanx shrink-0 mt-0.5" />
                <div>
                  <p className="text-xs uppercase tracking-wider text-slate-500">{L("Indirizzo", "Address", "Adresse")}</p>
                  <p className="mt-1 text-slate-200" data-testid="company-address">Le Mairekade 77, 1013 CB Amsterdam, Netherlands</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Building2 className="h-5 w-5 text-cyanx shrink-0 mt-0.5" />
                <div>
                  <p className="text-xs uppercase tracking-wider text-slate-500">{L("KvK (Camera di Commercio olandese)", "KvK (Dutch Chamber of Commerce)", "KvK (Niederländische Handelskammer)")}</p>
                  <p className="mt-1 text-slate-200 font-mono" data-testid="company-kvk">75927276</p>
                </div>
              </div>
            </div>
          </div>
          <div className="rounded-xl overflow-hidden border border-white/10 min-h-[300px] relative">
            <iframe
              title={L("Mappa sede", "Office map", "Standortkarte")}
              data-testid="company-map"
              src="https://www.openstreetmap.org/export/embed.html?bbox=4.8700%2C52.3905%2C4.8880%2C52.4000&layer=mapnik&marker=52.3948%2C4.8788"
              className="w-full h-full min-h-[300px]"
              style={{ border: 0 }}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
            <a
              href="https://www.google.com/maps/search/?api=1&query=Le%20Mairekade%2077%2C%201013%20CB%20Amsterdam%2C%20Netherlands"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="company-map-link"
              className="absolute bottom-3 right-3 rounded-md bg-navy/90 border border-white/15 px-3 py-1.5 text-xs text-cyanx hover:text-white backdrop-blur"
            >
              {L("Apri in Google Maps", "Open in Google Maps", "In Google Maps öffnen")}
            </a>
          </div>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <Reveal>
            <div className="rounded-2xl overflow-hidden border border-white/10">
              <img src={ABOUT_IMG} alt={L("Il team", "The team", "Das Team")} loading="lazy" className="w-full h-72 object-cover opacity-80" />
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="font-heading text-2xl font-medium text-white">{L("La nostra missione", "Our mission", "Unsere Mission")}</h2>
            <p className="mt-4 text-slate-400 leading-relaxed">
              {L("Aiutiamo privati, aziende, avvocati, notai ed eredi a valutare la fattibilità tecnica del recupero dell'accesso a un wallet, con processi trasparenti e documentati. Non promettiamo risultati garantiti e lavoriamo esclusivamente con la prova della titolarità.", "We help individuals, companies, lawyers, notaries and heirs assess the technical feasibility of recovering access to a wallet, with transparent, documented processes. We do not promise guaranteed results and work exclusively with proof of ownership.", "Wir helfen Privatpersonen, Unternehmen, Anwälten, Notaren und Erben, die technische Machbarkeit der Wiederherstellung des Wallet-Zugriffs zu bewerten – mit transparenten, dokumentierten Prozessen. Wir versprechen keine garantierten Ergebnisse und arbeiten ausschließlich mit Eigentumsnachweis.")}
            </p>
          </Reveal>
        </div>
        <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          {why.map((w, i) => (
            <Reveal key={w.title} delay={i * 0.05}>
              <div className="h-full rounded-xl border border-white/10 bg-navy-paper p-6">
                <w.icon className="h-6 w-6 text-cyanx" />
                <h3 className="mt-4 font-heading text-lg font-medium text-white">{w.title}</h3>
                <p className="mt-2 text-sm text-slate-400">{w.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <div className="mt-16 rounded-xl border border-white/10 bg-navy-paper p-8">
          <h3 className="font-heading text-xl font-medium text-white mb-4">{L("Principi e trasparenza", "Principles and transparency", "Grundsätze und Transparenz")}</h3>
          <ul className="space-y-2.5">
            {compliance.map((c) => (
              <li key={c} className="text-sm text-slate-400 border-l-2 border-white/10 pl-4">{c}</li>
            ))}
          </ul>
        </div>
      </section>
    </div>
  );
}
